import { apiRequest } from './client'
import { type SupportedLocale, normalizeLocale } from '../i18n/supportedLocales'

interface PreferredLocaleResponse {
  preferred_locale: string | null
}

/**
 * Fetch the backend-owned preferred UI locale for the authenticated user.
 *
 * Returns `null` when the user has no stored preference yet — callers MUST treat
 * that as "keep the current locale", not "switch to English", so a user whose
 * row hasn't been written yet is never flipped away from their local choice.
 *
 * This is the single source of truth for a user's persisted UI-locale
 * preference. It is S1-1-safe: the backend reads only
 * `public.user_preferred_locales`, never JWT `user_metadata`.
 */
export async function getBackendPreferredLocale(): Promise<SupportedLocale | null> {
  const response = await apiRequest<PreferredLocaleResponse>('/user/preferred-locale', {
    method: 'GET',
    retryTransient: false,
  })
  if (response.preferred_locale == null) return null
  return normalizeLocale(response.preferred_locale) ?? null
}

/**
 * Persist the active UI locale to the backend-owned locale store.
 *
 * `public.user_preferred_locales` is the single persisted store for a user's
 * UI-locale preference (it replaced the former GoTrue `user_metadata` write).
 * The same value drives request-scoped surfaces (via the `X-Dreamwrite-UI-Locale`
 * header for live requests) and async/server-initiated surfaces (emails, jobs)
 * that read the table directly. S1-1-safe: backend readers never read JWT
 * `user_metadata`.
 */
export async function putBackendPreferredLocale(locale: string): Promise<SupportedLocale> {
  const normalized = normalizeLocale(locale)
  if (!normalized) {
    throw new Error(`unsupported preferred locale: ${locale}`)
  }
  const response = await apiRequest<PreferredLocaleResponse>('/user/preferred-locale', {
    method: 'PUT',
    body: JSON.stringify({ preferred_locale: normalized }),
    retryTransient: false,
  })
  return normalizeLocale(response.preferred_locale ?? undefined) ?? normalized
}
